import { useId, useState } from 'react'
import { CheckCircle2, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatRelativeDate, toDate } from '@/lib/formatRelativeDate'
import type { ContextGap } from '@/types/api'

interface ResolvedGapsSectionProps {
  /** Resolved gaps, already sorted most recently resolved first. */
  gaps: ContextGap[]
}

const RESOLUTION_REASON: Record<string, string> = {
  interview_session: 'Closed by your answer',
  inference: 'Covered by what you uploaded',
}

/** Plain-language reason a gap closed. Unknown sources read "Resolved". */
export function resolutionReason(resolvedBy: string | null | undefined): string {
  return (resolvedBy && RESOLUTION_REASON[resolvedBy]) || 'Resolved'
}

/**
 * Read-only history of closed gaps below the active accordion. Collapsed
 * by default so finished work doesn't crowd what's left to do; renders
 * nothing when there's no history yet. The toggle sits inside an h2 so
 * the section shows up in heading navigation.
 */
export function ResolvedGapsSection({ gaps }: ResolvedGapsSectionProps) {
  const [open, setOpen] = useState(false)
  const listId = useId()

  if (gaps.length === 0) return null

  return (
    <section className="mt-8" data-testid="resolved-gaps-section">
      <h2 className="m-0">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((prev) => !prev)}
          data-testid="resolved-gaps-toggle"
          className={cn(
            'flex items-center gap-2 min-h-[44px] rounded-md px-1 text-sm font-display font-semibold',
            'text-text-secondary hover:text-text-primary transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
        >
          <ChevronDown
            size={16}
            aria-hidden
            className={cn(
              'shrink-0 transition-transform motion-reduce:transition-none',
              open && 'rotate-180'
            )}
          />
          Resolved ({gaps.length})
        </button>
      </h2>
      {open && (
        <ul id={listId} className="mt-2 flex flex-col gap-2">
          {gaps.map((gap) => (
            <ResolvedGapItem key={gap.id} gap={gap} />
          ))}
        </ul>
      )}
    </section>
  )
}

function ResolvedGapItem({ gap }: { gap: ContextGap }) {
  const title = gap.title?.trim() || gap.description || 'Resolved gap'
  const when = formatRelativeDate(gap.resolvedAt)

  return (
    <li
      data-testid={`resolved-gap-${gap.id}`}
      className="flex items-start gap-3 rounded-lg border border-[color:color-mix(in_srgb,var(--success)_40%,transparent)] bg-bg-primary px-4 py-3"
    >
      <CheckCircle2
        size={16}
        aria-hidden
        className="mt-0.5 shrink-0 text-success"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-display font-semibold text-text-secondary line-clamp-2 break-words">
          {title}
        </p>
        <p className="mt-0.5 text-xs text-text-secondary">
          {resolutionReason(gap.resolvedBy)}
          {when && (
            <span className="text-text-tertiary">
              {' · '}
              <time
                className="font-mono"
                dateTime={toDate(gap.resolvedAt)?.toISOString()}
              >
                {when}
              </time>
            </span>
          )}
        </p>
      </div>
      {gap.domain && (
        <span className="hidden sm:inline-flex shrink-0 items-center px-2 py-0.5 rounded font-mono text-xs text-text-tertiary bg-bg-elevated border border-border-default">
          {gap.domain.replace(/_/g, ' ')}
        </span>
      )}
    </li>
  )
}
